import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  BackrsSidebar,
  ConfirmModal,
  lottieAnimations,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EMULATION_MARGIN,
  SIDEBAR_EXPANDED_WIDTH,
  SIDEBAR_MOBILE_HEIGHT,
  WidthToken,
  WIDTH,
  MobileNavItem,
  visuallyHiddenSx,
} from '@backrs_ui/common-ui';

import { AgentContextProvider } from '@backrs_ui/grow-ui/src/agentContext/AgentContextProvider';
import {
  APP_ROUTE,
  AppRoute,
  COACHING_ROUTE_PATHS,
  DEFAULT_SHELL_MODE,
  PageBackground,
  LegacyPageTypes,
  LubavPanelSize,
  resolveRouteValue,
} from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings, formatPageTitle } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { AssigneeEmulationPalette } from '@backrs_ui/grow-ui/src/components/AssigneeEmulationPalette';
import { GettingStartedModal } from '@backrs_ui/grow-ui/src/components/GettingStartedModal/GettingStartedModal';
import { useGettingStartedModal } from '@backrs_ui/grow-ui/src/components/GettingStartedModal/useGettingStartedModal';
import { useResizableDivider } from '@backrs_ui/grow-ui/src/components/Lubav/hooks/useResizableDivider';
import { LUBAV_PANEL_DEFAULT_WIDTH_PCT } from '@backrs_ui/grow-ui/src/components/Lubav/ResizableDivider';
import { ProfileCompletionModal } from '@backrs_ui/grow-ui/src/components/ProfileCompletionModal/ProfileCompletionModal';
import {
  AutoModalHoldProvider,
  useProfileCompletionModal,
} from '@backrs_ui/grow-ui/src/components/ProfileCompletionModal/useProfileCompletionModal';
import { SignupPromptModal } from '@backrs_ui/grow-ui/src/components/SignupPromptModal/SignupPromptModal';
import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import { useDefaultOperatorContext } from '@backrs_ui/grow-ui/src/hooks/useDefaultOperatorContext';
import { useNewVersionCheck } from '@backrs_ui/grow-ui/src/hooks/useNewVersionCheck';
import { usePageModal } from '@backrs_ui/grow-ui/src/hooks/usePageModal';
import { usePageScroll } from '@backrs_ui/grow-ui/src/hooks/usePageScroll';
import { useSessionToolTitle } from '@backrs_ui/grow-ui/src/hooks/useSessionToolTitle';
import { LUBAV_SUPERSESSIONS } from '@backrs_ui/grow-ui/src/models/models';
import { CoachNoteEditorHost } from '@backrs_ui/grow-ui/src/pages/CoachingDashboard/CoachNotes/CoachNoteEditorHost';
import {
  StickerCardId,
  useToolCardById,
} from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';
import { ChatBox } from '@backrs_ui/grow-ui/src/pages/OfficeHours/molecules/ChatBox';
import { PlaylistsDrawer } from '@backrs_ui/grow-ui/src/pages/Priorities/molecules/PlaylistsDrawer';
import { PrioritiesForm } from '@backrs_ui/grow-ui/src/pages/Priorities/molecules/PrioritiesForm';
import { SubscriptionPageContainer } from '@backrs_ui/grow-ui/src/pages/Subscription/SubscriptionPageContainer';
import {
  dismissSnackbar,
  selectHasNewVersion,
  selectPageTitle,
  selectSnackbar,
} from '@backrs_ui/grow-ui/src/redux/appState/appState';
import { selectIsEmulating } from '@backrs_ui/grow-ui/src/redux/emulator/emulator';
import {
  FEATURE_FLAGS,
  selectIsFeatureFlagEnabled,
} from '@backrs_ui/grow-ui/src/redux/featureFlags/featureFlags';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { openMobileFab } from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState';
import {
  deactivateSharedSession,
  selectIsSharedSessionActive,
} from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSession';
import { selectUser } from '@backrs_ui/grow-ui/src/redux/user/user';
import { useFetchUserPermissionsQuery } from '@backrs_ui/grow-ui/src/redux/user/user.api';
import { IS_SANDBOX } from '@backrs_ui/grow-ui/src/utils/sandboxSession';
import { BUILD_NUMBER, VERSION } from '@backrs_ui/grow-ui/src/version/version';

import { Alert, Box, Grid, Snackbar, SxProps, Theme, useMediaQuery } from '@mui/material';
import Lottie from 'lottie-react';
import { usePostHog } from 'posthog-js/react';

import { MobileLubavFAB } from './MobileLubavFAB';
import { resolvePageBackground } from './pageShell.backgrounds';
import { usePreLinkedParams } from './pageShell.hooks';
import { CONTENT_GUTTER, PAGE_SCROLL_ID } from './pageShell.layout';
import { RESIZABLE_CONTAINER_ID, ResizablePanelLayout } from './ResizablePanelLayout';
import { useAgentContext } from './resourceTabs/PageShellTabs.agentContext';
import { ShellContentTabs } from './resourceTabs/ShellContentTabs';
import { ShellResourceContent } from './resourceTabs/ShellResourceContent';
import { useShellResourceTabs } from './resourceTabs/useShellResourceTabs';
import { useMenuSections } from './sections/menuSections.hook';
import { getSessionBanner } from './SessionBanner';
import { SHELL_HEADER_COMPACT_QUERY, ShellHeader } from './ShellHeader';
import { StructuredSessionActionButtons } from './StructuredSessionActionButtons';
import { useCurrentAppRoute } from './useCurrentAppRoute';
import { useSessionSwitchPrompt } from './useSessionSwitchPrompt';
import { useShellSession } from './useShellSession';

const MemoizedBackrsSidebar = memo(BackrsSidebar);

const LUBAV_PANEL_INITIAL_WIDTH: Record<LubavPanelSize, number | undefined> = {
  small: LUBAV_PANEL_DEFAULT_WIDTH_PCT,
  large: LUBAV_PANEL_DEFAULT_WIDTH_PCT,
  hidden: undefined,
};

export const PageShellProvider = memo(() => {
  const currentRoute = useCurrentAppRoute();

  return (
    <AgentContextProvider
      currentRoutePath={currentRoute?.path}
      currentRouteSubRoute={currentRoute?.subRoute}
    >
      <PageShell currentRoute={currentRoute} />
    </AgentContextProvider>
  );
});

const PageShell = memo(({ currentRoute }: { currentRoute: AppRoute | undefined }) => {
  const location = useLocation();
  const params = useParams();

  const shellMode = currentRoute?.shellMode ?? DEFAULT_SHELL_MODE;
  const lubavPanel = resolveRouteValue(shellMode.lubavPanel, params, location.pathname) ?? 'small';
  const isOnSharedRoute = location.pathname.startsWith('/shared/');

  // Paywall detection — must come before showLubav and useMenuSections
  const user = useAppSelector(selectUser);
  const { data: userPermissions } = useFetchUserPermissionsQuery(undefined, {
    skip: !user?.id,
  });
  const isGrowPaywallEnabled = useAppSelector(
    selectIsFeatureFlagEnabled(FEATURE_FLAGS.GROW_PAYWALL),
  );
  const hasAccessGrow = userPermissions?.access_grow?.allowed === true;
  const isPaywalled =
    !!user?.id && isGrowPaywallEnabled && userPermissions !== undefined && !hasAccessGrow;

  const showLubav = lubavPanel !== 'hidden' && !isPaywalled;

  const {
    activeSession,
    isSharedSessionRoute,
    handleNewChat,
    handleRestartStructuredSession,
    handleNewOfficeHoursSession,
    isTyping,
    isStreaming,
    isOfficeHoursSessionId,
    isStructured,
    isResolvingRecall,
    sessionTitle,
  } = useShellSession(showLubav);

  const { switchModal, dismissSwitch } = useSessionSwitchPrompt();

  const navigate = useNavigate();

  const { getStickerCard } = useToolCardById({ navigate });

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isDesktop = !isMobile;
  // BackrsSidebar switches from bottom bar to left drawer at 601px
  const isSidebarOnLeft = useMediaQuery('(min-width: 601px)');
  const isHeaderCompact = useMediaQuery(SHELL_HEADER_COMPACT_QUERY);
  const showLeftPanel = isDesktop && lubavPanel !== 'hidden';
  const isHeaderSearchShown = showLeftPanel && !isHeaderCompact;

  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const {
    leftWidth: panelLeftWidth,
    isResizing: panelIsResizing,
    isCollapsed: panelIsCollapsed,
    handleMouseDown: panelHandleMouseDown,
    handleExpandMouseDown: panelHandleExpandMouseDown,
    expand: expandPanel,
  } = useResizableDivider({
    containerId: RESIZABLE_CONTAINER_ID,
    isMobile,
    initialWidth: LUBAV_PANEL_INITIAL_WIDTH[lubavPanel],
  });

  const handleNewChatAndExpand = useCallback(() => {
    if (panelIsCollapsed) expandPanel();

    handleNewChat();
  }, [panelIsCollapsed, expandPanel, handleNewChat]);

  const queuedPrompt = useAppSelector((state) => state.sessionState.queuedPrompt);

  const dispatch = useAppDispatch();
  const postHog = usePostHog();

  useNewVersionCheck();
  const hasNewVersion = useAppSelector(selectHasNewVersion);
  const snackbar = useAppSelector(selectSnackbar);
  const hasFiredSidebarShownRef = useRef(false);
  // Auto-open the sidebar the first time a new-version card becomes available so the
  // user actually notices it. On desktop this opens the permanent left drawer; on mobile
  // it opens the hamburger overlay. They can still close it; we only re-fire on the
  // next false→true transition (e.g. rollback then redeploy).
  const hasAutoOpenedForNewVersionRef = useRef(false);

  useEffect(() => {
    const cardVisible = hasNewVersion && !isPaywalled;

    if (cardVisible && !hasFiredSidebarShownRef.current) {
      hasFiredSidebarShownRef.current = true;
      postHog?.capture('version_update_shown', {
        surface: 'sidebar',
        version: `${VERSION}.${BUILD_NUMBER}`,
      });
    } else if (!cardVisible) {
      hasFiredSidebarShownRef.current = false;
    }
  }, [hasNewVersion, isPaywalled, postHog]);

  useEffect(() => {
    if (!hasNewVersion) {
      hasAutoOpenedForNewVersionRef.current = false;

      return;
    }

    if (isPaywalled || hasAutoOpenedForNewVersionRef.current) return;

    hasAutoOpenedForNewVersionRef.current = true;
    if (isMobile) {
      setMobileSidebarOpen(true);
    } else {
      setDesktopSidebarOpen(true);
    }
  }, [hasNewVersion, isMobile, isPaywalled]);

  // Clear the shared-session token once the user navigates off /shared/* — otherwise
  // the share token keeps winning over the user's access token in baseQuery.getAccessToken,
  // and Lubav/solution-session calls run against the share owner's identity instead of the viewer's.
  const isSharedSessionActive = useAppSelector(selectIsSharedSessionActive);

  useEffect(() => {
    if (!isOnSharedRoute && isSharedSessionActive) {
      dispatch(deactivateSharedSession());
    }
  }, [isOnSharedRoute, isSharedSessionActive, dispatch]);

  const isOfficeHoursRoute =
    location.pathname === '/' || location.pathname.startsWith('/office-hours/');

  const isEmulating = useAppSelector(selectIsEmulating);
  const showEmulationIndicator = isPaywalled ? false : isEmulating;

  useDefaultOperatorContext();

  // Compute the current "gutter" that content should respect. An emulating sidebar is inset,
  // so its footprint grows by its margin on each horizontal edge.
  const sidebarGutter = useMemo(() => {
    if (!isSidebarOnLeft) {
      return '0px';
    }

    const width = desktopSidebarOpen ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH;

    return showEmulationIndicator
      ? `calc(${width}px + ${SIDEBAR_EMULATION_MARGIN} * 2)`
      : `${width}px`;
  }, [desktopSidebarOpen, isSidebarOnLeft, showEmulationIndicator]);

  const { isGuest } = useAuth();
  const profileCompletion = useProfileCompletionModal({
    enabled: !!user?.id && !isPaywalled && currentRoute?.path !== APP_ROUTE.SIGN_UP,
  });

  const gettingStartedEnabled =
    (!!user?.id || isGuest) && !isPaywalled && currentRoute?.path !== APP_ROUTE.SIGN_UP;
  // The critical-information prompt comes first in the auto-modal chain.
  const gettingStarted = useGettingStartedModal({
    enabled: gettingStartedEnabled,
    autoShow: !profileCompletion.blocking,
  });

  const {
    sidebarMenu: sidebar,
    isAssigneeEmulationPaletteOpen,
    setIsAssigneeEmulationPaletteOpen,
  } = useMenuSections(
    isPaywalled,
    panelIsCollapsed,
    expandPanel,
    gettingStarted.onOpen,
    () => handleNewOfficeHoursSession(APP_ROUTE.HOME),
    isHeaderSearchShown,
  );

  usePreLinkedParams(dispatch, postHog);

  const mobileNavItems: MobileNavItem[] = useMemo(
    () => [
      { iconName: 'Map01', onClick: () => navigate(APP_ROUTE.MY_BLUEPRINT) },
      { iconName: 'PuzzlePiece01', onClick: () => navigate(APP_ROUTE.TOOLS) },
      ...(userPermissions?.access_deep_dives.allowed
        ? [{ iconName: 'StickerCircle' as const, onClick: () => navigate(APP_ROUTE.DEEP_DIVES) }]
        : []),
    ],
    [navigate, userPermissions?.access_deep_dives.allowed],
  );

  const legacyPageType: LegacyPageTypes | undefined = currentRoute?.legacyPageType ?? undefined;
  const background =
    resolveRouteValue(currentRoute?.background, params, location.pathname) ?? 'default';
  const showMobileFAB = isMobile && lubavPanel !== 'hidden' && (!!user?.id || isGuest);
  const isCoachingMemberRoute = currentRoute?.path === APP_ROUTE.COACHING_DASHBOARD_MEMBER;
  const isCoachingRoute = !!currentRoute && COACHING_ROUTE_PATHS.includes(currentRoute.path);
  const resourceTabsOnly = shellMode.layout === 'resourceTabsOnly';
  const supercoach = activeSession === LUBAV_SUPERSESSIONS.SUPERCOACH;
  const superuser = activeSession === LUBAV_SUPERSESSIONS.SUPERUSER;
  const routeMaxWidth = currentRoute?.maxWidth;

  // Driven by the queued state, not dispatched with it: a producer's effect flushes before
  // MobileLubavFAB's collapse-on-navigation, which would undo it.
  useEffect(() => {
    if (!queuedPrompt) return;

    if (showMobileFAB) dispatch(openMobileFab());
    else if (panelIsCollapsed) expandPanel();
  }, [queuedPrompt, showMobileFAB, panelIsCollapsed, expandPanel, dispatch]);

  const pageTitle = useAppSelector(selectPageTitle);

  const routePageTitle = useMemo(
    () => resolveRouteValue(currentRoute?.title, params, location.pathname) ?? 'Backrs',
    [currentRoute, params, location.pathname],
  );

  const sessionToolTitle = useSessionToolTitle(activeSession);

  const isSessionRoute =
    currentRoute?.path === APP_ROUTE.OFFICE_HOURS_SESSION ||
    currentRoute?.path === APP_ROUTE.SOLUTION_SESSION;

  const resolvedPageTitle =
    pageTitle ?? (isSessionRoute ? sessionToolTitle : undefined) ?? routePageTitle;

  useEffect(() => {
    document.title =
      !currentRoute?.title || resolvedPageTitle === 'Home'
        ? 'Backrs'
        : formatPageTitle(resolvedPageTitle);
  }, [resolvedPageTitle, currentRoute]);

  const resourceTabs = useShellResourceTabs({ shellMode });

  usePageScroll(resourceTabs.showResourceContent);

  useAgentContext(
    resourceTabs.showResourceContent ? resourceTabs.selectedBoxType : undefined,
    resourceTabs.contentProps.sortedContent,
  );

  const namedSessionCard =
    isOfficeHoursSessionId && activeSession ? getStickerCard(activeSession as StickerCardId) : null;

  const shellModal = usePageModal(currentRoute?.path, {
    autoOpen: !profileCompletion.blocking,
  });

  const backrs40GridLayout = (
    <Box sx={backrs40OuterSx(sidebarGutter, SIDEBAR_MOBILE_HEIGHT, background)}>
      {showLeftPanel && (
        <ShellHeader
          title={sessionToolTitle || sessionTitle}
          description={namedSessionCard?.description}
          userFirstName={user?.first_name}
          isEmulating={showEmulationIndicator}
          showOperatingAs
          onNewChat={isSharedSessionRoute ? undefined : handleNewChatAndExpand}
          onRestartSession={handleRestartStructuredSession}
          onNewOfficeHoursSession={handleNewOfficeHoursSession}
          confirmNewChat={isStructured}
          onSearch={sidebar?.search.onSearch}
          onGettingStartedClick={gettingStarted.onOpen}
          // Get Started button is for normal users. Coaches don't need it on the coaching dashboard
          hideGettingStarted={isCoachingMemberRoute}
          disabled={isTyping || isStreaming}
          leftPanelWidth={panelLeftWidth}
          isResizing={panelIsResizing}
          isPanelCollapsed={panelIsCollapsed}
          actionButtons={
            namedSessionCard && !getSessionBanner(activeSession) ? (
              <StructuredSessionActionButtons />
            ) : null
          }
          showSandboxContact={IS_SANDBOX && isCoachingMemberRoute}
          includeKeyLevers={isCoachingRoute}
          back={
            isCoachingMemberRoute
              ? {
                  label: appStrings.modules.coachingDashboard.mainTitle,
                  onClick: () => {
                    const from = (location.state as { from?: string } | null)?.from;
                    const fromPathname = from?.split('?')[0];

                    navigate(
                      fromPathname === APP_ROUTE.COACHING_DASHBOARD && from
                        ? from
                        : APP_ROUTE.COACHING_DASHBOARD,
                    );
                  },
                }
              : undefined
          }
        />
      )}
      <ResizablePanelLayout
        showPanel={showLeftPanel}
        isCollapsed={panelIsCollapsed}
        showMobileFAB={showMobileFAB}
        leftWidth={panelLeftWidth}
        isResizing={panelIsResizing}
        onDividerMouseDown={panelHandleMouseDown}
        onExpandMouseDown={panelHandleExpandMouseDown}
        leftPanel={<ChatBox key={activeSession} superuser={superuser} supercoach={supercoach} />}
      >
        <Box sx={pageBodyLayoutSx}>
          {isResolvingRecall ? (
            <Box sx={recallLoadingSx}>
              <Lottie animationData={lottieAnimations.dotsDotting} loop />
            </Box>
          ) : (
            <>
              {showLubav && (resourceTabs.hasResourceTabs || !resourceTabsOnly) && (
                <ShellContentTabs {...resourceTabs.tabsProps} pageTitle={resolvedPageTitle} />
              )}
              <Box id={PAGE_SCROLL_ID} sx={bodyScrollerSx(showLubav)}>
                <ShellResourceContent resourceTabs={resourceTabs} />
                <Box
                  id="backrs-scroll-container"
                  sx={scrollContainerSx}
                  display={resourceTabs.showResourceContent ? 'none' : undefined}
                >
                  <Grid container columns={9} sx={getGridSx(routeMaxWidth)}>
                    <Outlet />
                  </Grid>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </ResizablePanelLayout>
      {showMobileFAB && (
        <MobileLubavFAB
          activeSession={activeSession}
          sessionTitle={sessionTitle}
          handleNewChat={isSharedSessionRoute ? undefined : handleNewChat}
          onRestartSession={handleRestartStructuredSession}
          onNewOfficeHoursSession={handleNewOfficeHoursSession}
          confirmNewChat={isStructured}
          isTyping={isTyping}
          isStreaming={isStreaming}
          defaultExpanded={isOfficeHoursRoute}
        />
      )}
      {shellModal.modalNode}
      <CoachNoteEditorHost />
      {switchModal && (
        <ConfirmModal
          open
          onClose={dismissSwitch}
          variant="warning"
          headerText={switchModal.headerText}
          bodyText={switchModal.bodyText}
          primaryAction={switchModal.primaryAction}
          secondaryAction={switchModal.secondaryAction}
        />
      )}
    </Box>
  );

  const unauthenticatedAndLegacyLayout = (
    <Box sx={getPageBodySx(legacyPageType, SIDEBAR_MOBILE_HEIGHT, background)}>
      <Outlet />
    </Box>
  );

  const paywallLayout = (
    <Box sx={backrs40OuterSx(sidebarGutter, SIDEBAR_MOBILE_HEIGHT, 'default')}>
      <Box sx={pageBodyLayoutSx}>
        <Box id={PAGE_SCROLL_ID} sx={bodyScrollerSx(false)}>
          <Box id="backrs-scroll-container" sx={scrollContainerSx}>
            <Grid container columns={9} sx={getGridSx(undefined)}>
              <SubscriptionPageContainer />
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const resolvedLayout = isPaywalled
    ? paywallLayout
    : legacyPageType === 'BACKRS_4.0'
      ? backrs40GridLayout
      : unauthenticatedAndLegacyLayout;

  return (
    <AutoModalHoldProvider value={profileCompletion.blocking}>
      {user?.id && !isPaywalled && <PlaylistsDrawer />}
      {user?.id && !isPaywalled && <PrioritiesForm />}

      <SignupPromptModal />

      <ProfileCompletionModal
        key={user?.id ?? 'signed-out'}
        open={profileCompletion.open}
        onClose={profileCompletion.onClose}
        profile={profileCompletion.profile}
        profileOptions={profileCompletion.profileOptions}
        graduated={profileCompletion.graduated}
      />

      {gettingStartedEnabled && (
        <GettingStartedModal
          open={gettingStarted.open}
          onClose={gettingStarted.onClose}
          onAcknowledge={gettingStarted.onAcknowledge}
        />
      )}

      {!isPaywalled && (
        <AssigneeEmulationPalette
          open={isAssigneeEmulationPaletteOpen}
          onClose={() => setIsAssigneeEmulationPaletteOpen(false)}
        />
      )}

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={6000}
        onClose={() => dispatch(dismissSnackbar())}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          id="page-shell-snackbar-alert"
          severity={snackbar?.severity ?? 'error'}
          icon={false}
          sx={({ typography }) => ({ ...typography['text-sm-medium'] })}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>

      {hasNewVersion && !isPaywalled && (
        <Box aria-live="polite" sx={visuallyHiddenSx}>
          {appStrings.modules.backrsPageShell.menuItems.sidebar.newVersion.heading}
        </Box>
      )}

      {/* Provide CSS var for all descendants to read without prop-drilling */}
      <Box sx={{ ...pageShellStyle, '--sidebar-gutter': sidebarGutter } as SxProps<Theme>}>
        {sidebar && legacyPageType !== 'UNAUTHENTICATED' && (
          <MemoizedBackrsSidebar
            sidebar={sidebar}
            isEmulating={showEmulationIndicator}
            controlledDesktopOpen={desktopSidebarOpen}
            onDesktopOpenChange={setDesktopSidebarOpen}
            controlledMobileOpen={mobileSidebarOpen}
            onMobileOpenChange={setMobileSidebarOpen}
            mobileNavItems={isPaywalled ? [] : mobileNavItems}
          />
        )}

        {resolvedLayout}
      </Box>
    </AutoModalHoldProvider>
  );
});

/** ————— Layout / Style helpers ————— */

const pageShellStyle: SxProps<Theme> = {
  display: 'block', // Drawer is fixed; content flows normally with margin-left var below
  height: '100svh',
  overflow: { xs: 'hidden', sm: 'unset' },
};

const backrs40OuterSx = (
  sidebarGutter: string,
  sidebarMobileHeightGutterPx: number,
  background: PageBackground,
): SxProps<Theme> => [
  {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: { xs: `calc(100svh - ${sidebarMobileHeightGutterPx}px)`, sm: '100svh' },
    marginTop: { xs: `${sidebarMobileHeightGutterPx}px`, sm: 'unset' },
    paddingLeft: { sm: sidebarGutter, md: 'var(--sidebar-gutter, 0px)' },
    transition: 'padding-left 0.3s ease-in-out',
  },
  resolvePageBackground(background),
];

/** Centralized page-body style chooser that honors the CSS var gutter. */
const getPageBodySx = (
  legacyPageType: LegacyPageTypes | undefined,
  sidebarMobileHeightGutterPx: number,
  background: PageBackground,
): SxProps<Theme> => {
  switch (legacyPageType) {
    case 'UNAUTHENTICATED':
      return [{ width: '100%' }, resolvePageBackground(background)];
    case 'BACKRS_4.0':
    case 'LUBAV_4.0':
    default:
      return [
        {
          width: '100%',
          paddingLeft: { xs: '0px', sm: 'var(--sidebar-gutter, 0px)' },
          height: { xs: '100svh', sm: '100svh' },
          paddingTop: { xs: `${sidebarMobileHeightGutterPx}px`, sm: 'unset' },
          transition: 'padding-left 0.3s ease-in-out',
        },
        resolvePageBackground(background),
      ];
  }
};

const pageBodyLayoutSx: SxProps<Theme> = {
  minHeight: 0,
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const bodyScrollerSx =
  (lubavLayoutActive?: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    flex: 1,
    minHeight: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    paddingInline: lubavLayoutActive
      ? backrs.spacing[CONTENT_GUTTER]
      : {
          xs: backrs.spacing[CONTENT_GUTTER],
          md: backrs.spacing['spacing-4xl'],
          lg: backrs.spacing['spacing-5xl'],
        },
  });

const resolveMaxWidth = (maxWidth?: WidthToken | (string & {})): string | undefined => {
  if (!maxWidth) return undefined;
  if (maxWidth in WIDTH) return WIDTH[maxWidth as WidthToken];

  return maxWidth;
};

const scrollContainerSx: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  flex: 1,
  minHeight: 0,
};

const recallLoadingSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  flex: 1,
};

const getGridSx =
  (maxWidth?: WidthToken | (string & {})): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '100%',
    minHeight: '100%',
    margin: '0 auto',
    containerType: 'inline-size',
    maxWidth: resolveMaxWidth(maxWidth),
    pt: backrs.spacing['spacing-2xl'],
  });
